import Phaser from 'phaser';
import { EquipmentType, EQUIPMENT_DEFS, STARTING_BUDGET } from '@/config';
import { EventBus, GameEvent } from '@/utils/EventBus';
import { colorToHex } from '@/utils/ColorUtils';

/**
 * UIScene: HUD overlay rendered in screen space above the game world.
 * Contains build panel, budget display, and satisfaction meter.
 */
export class UIScene extends Phaser.Scene {
  private budget = STARTING_BUDGET;
  private budgetText!: Phaser.GameObjects.Text;
  private satisfactionText!: Phaser.GameObjects.Text;
  private raverCountText!: Phaser.GameObjects.Text;
  private muteButton!: Phaser.GameObjects.Text;
  private selectedType: EquipmentType | null = null;
  private buildButtons: Map<EquipmentType, Phaser.GameObjects.Container> = new Map();
  private demolishButton!: Phaser.GameObjects.Container;
  private isDemolishing = false;

  constructor() {
    super({ key: 'UIScene' });
  }

  create(): void {
    this.createBudgetDisplay();
    this.createStatsDisplay();
    this.createBuildPanel();
    this.createMuteButton();
    this.setupEventListeners();
  }

  private createBudgetDisplay(): void {
    this.budgetText = this.add.text(16, 16, `$${this.budget}`, {
      fontFamily: 'monospace',
      fontSize: '24px',
      color: '#00ff00',
      stroke: '#000000',
      strokeThickness: 4,
    });
    this.budgetText.setScrollFactor(0);
    this.budgetText.setDepth(1000);
  }

  private createStatsDisplay(): void {
    this.satisfactionText = this.add.text(16, 48, 'Satisfaction: 0%', {
      fontFamily: 'monospace',
      fontSize: '16px',
      color: '#ff00ff',
      stroke: '#000000',
      strokeThickness: 3,
    });
    this.satisfactionText.setScrollFactor(0);
    this.satisfactionText.setDepth(1000);

    this.raverCountText = this.add.text(16, 70, 'Ravers: 0', {
      fontFamily: 'monospace',
      fontSize: '16px',
      color: '#00ffff',
      stroke: '#000000',
      strokeThickness: 3,
    });
    this.raverCountText.setScrollFactor(0);
    this.raverCountText.setDepth(1000);
  }

  private createBuildPanel(): void {
    const panelX = 16;
    const panelY = this.cameras.main.height - 80;

    const types = Object.values(EquipmentType);
    types.forEach((type, index) => {
      const def = EQUIPMENT_DEFS[type];
      const x = panelX + index * 90;

      const container = this.add.container(x, panelY);

      // Background
      const bg = this.add.rectangle(0, 0, 80, 60, 0x1a1a2e, 0.9);
      bg.setStrokeStyle(2, 0x333366);
      bg.setOrigin(0, 0);

      // Name
      const name = this.add.text(40, 8, def.name, {
        fontFamily: 'monospace',
        fontSize: '10px',
        color: colorToHex(def.color),
        align: 'center',
      });
      name.setOrigin(0.5, 0);

      // Cost
      const cost = this.add.text(40, 24, `$${def.cost}`, {
        fontFamily: 'monospace',
        fontSize: '12px',
        color: '#aaaaaa',
        align: 'center',
      });
      cost.setOrigin(0.5, 0);

      // Color indicator
      const indicator = this.add.rectangle(40, 46, 20, 6, def.color);

      container.add([bg, name, cost, indicator]);
      container.setDepth(1000);

      // Make interactive
      bg.setInteractive({ useHandCursor: true });
      bg.on('pointerdown', () => this.selectBuildType(type));

      this.buildButtons.set(type, container);
    });

    // Demolish button
    const demolishX = panelX + types.length * 90;
    this.demolishButton = this.add.container(demolishX, panelY);

    const demoBg = this.add.rectangle(0, 0, 80, 60, 0x2e1a1a, 0.9);
    demoBg.setStrokeStyle(2, 0x663333);
    demoBg.setOrigin(0, 0);

    const demoText = this.add.text(40, 20, 'DEMOLISH', {
      fontFamily: 'monospace',
      fontSize: '10px',
      color: '#ff3333',
      align: 'center',
    });
    demoText.setOrigin(0.5, 0);

    const demoSubtext = this.add.text(40, 36, '50% refund', {
      fontFamily: 'monospace',
      fontSize: '8px',
      color: '#aa6666',
      align: 'center',
    });
    demoSubtext.setOrigin(0.5, 0);

    this.demolishButton.add([demoBg, demoText, demoSubtext]);
    this.demolishButton.setDepth(1000);

    demoBg.setInteractive({ useHandCursor: true });
    demoBg.on('pointerdown', () => this.toggleDemolish());
  }

  private createMuteButton(): void {
    const x = this.cameras.main.width - 16;
    const y = 16;

    this.muteButton = this.add.text(x, y, 'MUTE', {
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#00ffff',
      stroke: '#000000',
      strokeThickness: 3,
      backgroundColor: '#1a1a2ecc',
      padding: { x: 8, y: 4 },
    });
    this.muteButton.setOrigin(1, 0);
    this.muteButton.setDepth(1000);
    this.muteButton.setInteractive({ useHandCursor: true });
    this.muteButton.on('pointerdown', () => {
      EventBus.emit(GameEvent.TOGGLE_MUTE);
    });

    EventBus.on(GameEvent.MUTE_CHANGED, (muted: boolean) => {
      this.muteButton.setText(muted ? 'UNMUTE' : 'MUTE');
      this.muteButton.setColor(muted ? '#ff6666' : '#00ffff');
    });
  }

  private selectBuildType(type: EquipmentType): void {
    if (this.selectedType === type) {
      // Deselect
      this.selectedType = null;
      this.isDemolishing = false;
      this.updateButtonHighlights();
      EventBus.emit(GameEvent.BUILD_MODE_CHANGED, { type: null, demolish: false });
      return;
    }

    this.selectedType = type;
    this.isDemolishing = false;
    this.updateButtonHighlights();
    EventBus.emit(GameEvent.BUILD_MODE_CHANGED, { type, demolish: false });
  }

  private toggleDemolish(): void {
    this.isDemolishing = !this.isDemolishing;
    this.selectedType = null;
    this.updateButtonHighlights();
    EventBus.emit(GameEvent.BUILD_MODE_CHANGED, { type: null, demolish: this.isDemolishing });
  }

  private updateButtonHighlights(): void {
    for (const [type, container] of this.buildButtons) {
      const bg = container.getAt(0) as Phaser.GameObjects.Rectangle;
      if (type === this.selectedType) {
        bg.setStrokeStyle(2, 0x00ff00);
      } else {
        bg.setStrokeStyle(2, 0x333366);
      }
    }

    const demoBg = this.demolishButton.getAt(0) as Phaser.GameObjects.Rectangle;
    demoBg.setStrokeStyle(2, this.isDemolishing ? 0xff0000 : 0x663333);
  }

  private setupEventListeners(): void {
    EventBus.on(GameEvent.BUDGET_CHANGED, (balance: number) => {
      this.budget = balance;
      this.budgetText.setText(`$${Math.floor(this.budget)}`);
    });

    EventBus.on(GameEvent.SATISFACTION_CHANGED, (satisfaction: number) => {
      this.satisfactionText.setText(`Satisfaction: ${Math.round(satisfaction * 100)}%`);
    });

    EventBus.on(GameEvent.RAVER_COUNT_CHANGED, (count: number) => {
      this.raverCountText.setText(`Ravers: ${count}`);
    });
  }
}
